import type { MapFloorLevel } from './content/schema/map';

export interface PrefabInstanceDefinition {
  /** Must be a real registered object id (see objectRegistry.ts/coreObjects.tsx)
   * - these previously referenced ids that were never registered anywhere
   * ('prop_box_a', 'prop_cover_wall', 'prop_barrier', 'light_point'), so
   * every preset silently failed to place anything. A `light_`-prefixed id
   * is converted into a real MapLight, not a generic prop instance - see
   * the drop logic in editor-panels.tsx's DockPrefabs. */
  assetId: string;
  floor: MapFloorLevel;
  transform: {
    position: [number, number, number];
    rotation?: [number, number, number];
    scale?: [number, number, number];
  };
  name?: string;
  color?: string;
  intensity?: number;
}

/** Matches PREFAB_PRESET_CATEGORIES' tabs in editor-panels.tsx's DockPrefabs
 * exactly - a category with no matching tab is unreachable there (no way to
 * filter to it), so add a tab first if this ever needs a new member. */
export type PrefabCategory = 'tactical' | 'cover' | 'lighting';

export interface PrefabDefinition {
  id: string;
  name: string;
  category: PrefabCategory;
  description: string;
  icon: string;
  instances: PrefabInstanceDefinition[];
}

export const PREFABS: PrefabDefinition[] = [
  {
    id: 'bomb-site-alpha',
    name: 'Bomb Site A Setup',
    category: 'tactical',
    description: 'Bomb site zone with surrounding low/high cover blocks and overhead light rig.',
    icon: '🎯',
    instances: [
      {
        assetId: 'arch_floor_tile',
        floor: 0,
        transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [2, 0.4, 2] },
        name: 'Site A Platform',
      },
      {
        assetId: 'arch_wall',
        floor: 0,
        transform: { position: [-2.5, 0, 1.5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Site A Left Cover',
      },
      {
        assetId: 'arch_wall',
        floor: 0,
        transform: { position: [2.5, 0, 1.5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Site A Right Cover',
      },
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [0, 4.5, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Site A Overhead Light',
        color: '#34d399',
        intensity: 2.5,
      },
    ],
  },
  {
    id: 'mid-double-cover',
    name: 'Mid Double Cover',
    category: 'cover',
    description: 'Symmetrical L-shaped cover blocks for choke points and mid lanes.',
    icon: '🛡️',
    instances: [
      {
        assetId: 'arch_wall',
        floor: 0,
        transform: { position: [-1.2, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Mid Cover Left',
      },
      {
        assetId: 'arch_wall',
        floor: 0,
        transform: { position: [1.2, 0, 0], rotation: [0, Math.PI / 2, 0], scale: [1, 1, 1] },
        name: 'Mid Cover Right',
      },
    ],
  },
  {
    id: 'spawn-barrier-gate',
    name: 'Spawn Barrier Gate',
    category: 'tactical',
    description: 'Buy-phase spawn barrier wall with team indication lights.',
    icon: '🚧',
    instances: [
      {
        assetId: 'arch_wall',
        floor: 0,
        transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [4, 2.5, 0.4] },
        name: 'Spawn Barrier',
      },
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [-1.8, 3, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Barrier Light L',
        color: '#60a5fa',
        intensity: 1.8,
      },
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [1.8, 3, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Barrier Light R',
        color: '#60a5fa',
        intensity: 1.8,
      },
    ],
  },
  {
    id: 'ambient-light-rig',
    name: '4-Point Light Rig',
    category: 'lighting',
    description: 'Balanced 4-corner warm/cool lighting setup for indoor rooms.',
    icon: '💡',
    instances: [
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [-5, 4, -5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Light NW',
        color: '#fde68a',
        intensity: 1.5,
      },
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [5, 4, -5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Light NE',
        color: '#93c5fd',
        intensity: 1.5,
      },
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [-5, 4, 5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Light SW',
        color: '#93c5fd',
        intensity: 1.5,
      },
      {
        assetId: 'light_point_warm',
        floor: 0,
        transform: { position: [5, 4, 5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        name: 'Light SE',
        color: '#fde68a',
        intensity: 1.5,
      },
    ],
  },
];
